import { User } from '@adsk/alloy-provider';

import { ASSIGNEE_PICKER_USER } from '../consts';
import { useTranslation } from '../i18n';
import { ItemWithGroup } from '../types';

/**
 * Stable identity for an item across groups. `value` alone is not unique — it is
 * derived per group from `autodeskId` / `memberGroupId` / role id — so the group
 * is part of the key. Used for dropdown item identity, de-duplication, and the
 * selected-token React keys.
 *
 * Accepts null for compatibility with Downshift's `itemToKey` signature.
 */
export const getAssigneeItemKey = (item: ItemWithGroup | null) =>
  item ? `${item.group}:${item.value}` : '';

export const sortUsersAlphabetically = (items: User[]) =>
  items.toSorted((a, b) => a.name.localeCompare(b.name));

export type UserOptionSubtextRenderer = (user: User) => string;
export type IdToNameMap = Record<string, string>;
type LocaleReturnType = ReturnType<typeof useTranslation>['t'];

export const getRolesStr = (
  roleIds: string[] | undefined,
  roleIdToNameMap: IdToNameMap | undefined,
  t: LocaleReturnType,
) => {
  const roleNames =
    roleIds?.map((id) => roleIdToNameMap?.[id]).filter(Boolean) || [];

  if (roleNames.length <= 1) return roleNames.join();

  const lastRole = roleNames.at(-1);
  const remainingRoles = roleNames.slice(0, -1);
  return `${remainingRoles.join(', ')} ${t('AND')} ${lastRole}`;
};

export const getSubTitleString = (
  user: User,
  companyIdToNameMap: IdToNameMap,
  roleIdToNameMap: IdToNameMap,
  t: LocaleReturnType,
): string => {
  const { roleIds, companyId } = user || {};
  const rolesStr = getRolesStr(roleIds, roleIdToNameMap, t);
  const companyName = companyIdToNameMap?.[companyId];

  return rolesStr && companyName
    ? `${companyName}, ${rolesStr}`
    : rolesStr || companyName || '';
};

export const isUserGroup = (group: string) => group === ASSIGNEE_PICKER_USER;

/**
 * Determines if subtext is searchable (and should be highlighted) for the given group type.
 * Currently, only user subtexts are searchable/highlighted.
 * Company and role subtexts (member counts) are not searchable.
 *
 * This helper links the rendering logic in AssigneePickerMenuItem
 * with the business rule about which subtexts participate in search.
 *
 * @param group - The item group type (user, company, or role)
 * @returns true if subtext should be searchable/highlighted, false otherwise
 */
export const isSubtextSearchable = (group: string) => isUserGroup(group);
